import type { AiModelEnum, Annotation, EvidenceSessionAIModel } from 'api';
import { logger } from 'monitor';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { IGNORE_BALE_LABELS } from '../../constants';
import { type MobileZoom, renderCanvas } from '../../utils/canvas/canvas';
import { getGroupedAnnotations } from '../../utils/formatAnnotations/formatAnnotations';

interface UseCanvasRenderingProps {
  imageSrc: string;
  annotations: Annotation[];
  hiddenCategories: string[];
  model: AiModelEnum | EvidenceSessionAIModel;
  showLegend: boolean;
  isMobile: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  mobileZoom: MobileZoom;
}

interface UseCanvasRenderingReturn {
  isLoading: boolean;
  visibleAnnotations: Annotation[];
  labelColourMap: Map<string, readonly number[]>;
}

export const useCanvasRendering = ({
  imageSrc,
  annotations,
  hiddenCategories,
  model,
  showLegend,
  isMobile,
  canvasRef,
  mobileZoom,
}: UseCanvasRenderingProps): UseCanvasRenderingReturn => {
  const [isLoading, setIsLoading] = useState(true);

  const visibleAnnotations = useMemo(() => {
    if (!annotations) return [];
    return annotations.filter(({ label }) => !IGNORE_BALE_LABELS.includes(label) && !hiddenCategories.includes(label));
  }, [annotations, hiddenCategories]);

  const shouldDarken = useMemo(() => {
    return showLegend && visibleAnnotations.length > 0;
  }, [showLegend, visibleAnnotations]);

  const labelColourMap = useMemo(() => {
    const groupedAnnotations = getGroupedAnnotations(annotations, model);
    const colourMap = new Map<string, readonly number[]>();

    for (const group of groupedAnnotations) {
      for (const label of group.originalLabels) {
        colourMap.set(label, group.colour);
      }
    }

    return colourMap;
  }, [annotations, model]);

  const handleErrorRef = useRef(() => {
    setIsLoading(false);
    logger.error('Failed to render canvas');
  });

  const handleLoadStartRef = useRef(() => {
    setIsLoading(true);
  });

  const handleLoadEndRef = useRef(() => {
    setIsLoading(false);
  });

  handleErrorRef.current = () => {
    setIsLoading(false);
    logger.error('Failed to render canvas');
  };

  handleLoadStartRef.current = () => {
    setIsLoading(true);
  };

  handleLoadEndRef.current = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    return renderCanvas({
      annotations: visibleAnnotations,
      canvas,
      ctx,
      hiddenCategories,
      imageSrc,
      isMobile,
      labelColourMap,
      onError: handleErrorRef.current,
      onLoadEnd: handleLoadEndRef.current,
      onLoadStart: handleLoadStartRef.current,
      shouldDarken,
      zoom: mobileZoom,
    });
  }, [imageSrc, canvasRef, visibleAnnotations, hiddenCategories, labelColourMap, isMobile, shouldDarken, mobileZoom]);

  return {
    isLoading,
    labelColourMap,
    visibleAnnotations,
  };
};
